import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BedDouble,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CookingPot,
  DoorOpen,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  Minus,
  Plus,
  ShieldCheck,
  Sparkles,
  Users,
  Waves,
  X,
  XCircle,
} from "lucide-react";
import "./index.css";

import housePhoto from "./assets/photos/IMG_2545.webp";
import diningPhoto from "./assets/photos/IMG_2541.webp";
import loungePhoto from "./assets/photos/IMG_2544.webp";
import kitchenPhoto from "./assets/photos/IMG_2538.webp";
import meadowPhoto from "./assets/photos/IMG_2553.webp";
import pathPhoto from "./assets/photos/IMG_2560.webp";

type BookingStatus = "pending" | "approved" | "declined";
type Booking = {
  id: string;
  guestName: string;
  email: string;
  phone?: string;
  startDate: string;
  endDate: string;
  guests: number;
  message?: string;
  status: BookingStatus;
  createdAt: string;
};

const STATUS_LABEL: Record<BookingStatus, string> = { pending: "Väntande", approved: "Godkänd", declined: "Nekad" };
const FILTER_LABEL: Record<"all" | BookingStatus, string> = { all: "Alla", pending: "Väntande", approved: "Godkända", declined: "Nekade" };

const DAY = 86_400_000;
const pad = (value: number) => String(value).padStart(2, "0");
const toIsoDate = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromIsoDate = (value: string) => new Date(`${value}T12:00:00`);
const addDays = (date: Date, days: number) => new Date(date.getTime() + days * DAY);
const formatDate = (value: string, options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) =>
  new Intl.DateTimeFormat("sv-SE", options).format(fromIsoDate(value));

function Logo() {
  return (
    <a className="brand" href="#top" aria-label="Studentgården, startsida">
      <span className="brand-mark">S</span>
      <span>
        <strong>STUDENTGÅRDEN</strong>
        <small>SKANÖR · SEDAN 1939</small>
      </span>
    </a>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <Logo />
      <button className="menu-button" onClick={() => setOpen(!open)} aria-label="Växla meny"><Menu /></button>
      <nav className={open ? "nav-open" : ""}>
        <a href="#house" onClick={() => setOpen(false)}>Huset</a>
        <a href="#availability" onClick={() => setOpen(false)}>Tillgänglighet</a>
        <a href="#priser" onClick={() => setOpen(false)}>Priser</a>
        <a href="#gallery" onClick={() => setOpen(false)}>Galleri</a>
        <a className="nav-cta" href="#availability" onClick={() => setOpen(false)}>Boka vistelse <ArrowRight size={16} /></a>
      </nav>
    </header>
  );
}

function MiniCalendar({ bookings, selected, onSelect }: { bookings: Booking[]; selected: string; onSelect: (day: string) => void }) {
  const [cursor, setCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const days = new Date(year, month + 1, 0).getDate();
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const today = toIsoDate(new Date());
  const isBooked = (iso: string) => bookings.some((item) => item.status === "approved" && iso >= item.startDate && iso < item.endDate);
  return (
    <div className="calendar-card">
      <div className="calendar-head">
        <button aria-label="Föregående månad" onClick={() => setCursor(new Date(year, month - 1, 1))}><ChevronLeft /></button>
        <strong>{new Intl.DateTimeFormat("sv-SE", { month: "long", year: "numeric" }).format(cursor)}</strong>
        <button aria-label="Nästa månad" onClick={() => setCursor(new Date(year, month + 1, 1))}><ChevronRight /></button>
      </div>
      <div className="calendar-grid weekday-row">{["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((day) => <span key={day}>{day}</span>)}</div>
      <div className="calendar-grid calendar-days">
        {Array.from({ length: lead }).map((_, index) => <span key={`empty-${index}`} />)}
        {Array.from({ length: days }).map((_, index) => {
          const iso = toIsoDate(new Date(year, month, index + 1));
          const booked = isBooked(iso);
          const past = iso < today;
          return (
            <button key={iso} className={`${booked ? "booked" : ""} ${selected === iso ? "selected" : ""}`} disabled={past || booked} onClick={() => onSelect(iso)}>
              {index + 1}<i />
            </button>
          );
        })}
      </div>
      <div className="calendar-legend"><span><i className="available-dot" /> Ledigt</span><span><i className="booked-dot" /> Bokat</span></div>
    </div>
  );
}

function BookingForm({ bookings, initialStart }: { bookings: Booking[]; initialStart: string }) {
  const tomorrow = toIsoDate(addDays(new Date(), 1));
  const [form, setForm] = useState({ startDate: initialStart || tomorrow, endDate: toIsoDate(addDays(fromIsoDate(initialStart || tomorrow), 2)), guests: 2, guestName: "", email: "", phone: "", message: "" });
  const [state, setState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");
  useEffect(() => {
    if (initialStart) setForm((current) => ({ ...current, startDate: initialStart, endDate: toIsoDate(addDays(fromIsoDate(initialStart), 2)) }));
  }, [initialStart]);
  const nights = form.startDate && form.endDate ? Math.max(0, Math.round((fromIsoDate(form.endDate).getTime() - fromIsoDate(form.startDate).getTime()) / DAY)) : 0;
  const overlaps = bookings.some((item) => item.status === "approved" && form.startDate < item.endDate && form.endDate > item.startDate);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!nights || overlaps) { setState("error"); setError(overlaps ? "De datumen är redan bokade." : "Avresa måste vara efter ankomst."); return; }
    setState("loading");
    try {
      const response = await fetch("/api/bookings", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(form) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Kunde inte skicka förfrågan");
      setState("success");
    } catch (reason) {
      setState("error");
      setError(reason instanceof Error ? reason.message : "Kunde inte skicka förfrågan");
    }
  }

  if (state === "success") return (
    <div className="success-panel">
      <span><Check /></span>
      <p className="eyebrow">FÖRFRÅGAN MOTTAGEN</p>
      <h3>Vi hör av oss snart.</h3>
      <p>Din förfrågan om {nights} {nights === 1 ? "natt" : "nätter"} har skickats till gårdens personal. Du får ett mejl så snart den är granskad.</p>
      <button onClick={() => setState("idle")}>Gör en ny förfrågan</button>
    </div>
  );

  return (
    <form className="booking-form" onSubmit={submit}>
      <div className="form-heading"><div><p className="eyebrow">BOKA VISTELSE</p><h3>Planera ditt besök</h3></div>{nights > 0 && <span>{nights} {nights === 1 ? "natt" : "nätter"}</span>}</div>
      <div className="field-row">
        <label><span>Ankomst</span><input type="date" required min={tomorrow} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
        <label><span>Avresa</span><input type="date" required min={form.startDate || tomorrow} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label>
      </div>
      <label className="guest-control"><span>Gäster</span><div><button type="button" aria-label="Ta bort gäst" onClick={() => setForm({ ...form, guests: Math.max(1, form.guests - 1) })}><Minus /></button><strong>{form.guests}</strong><button type="button" aria-label="Lägg till gäst" onClick={() => setForm({ ...form, guests: Math.min(24, form.guests + 1) })}><Plus /></button></div></label>
      <div className="field-row">
        <label><span>Ditt namn</span><input required autoComplete="name" placeholder="Fullständigt namn" value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} /></label>
        <label><span>E-post</span><input required type="email" autoComplete="email" placeholder="du@exempel.se" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
      </div>
      <label><span>Telefon <em>valfritt</em></span><input type="tel" autoComplete="tel" placeholder="+46" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
      <label><span>Berätta om din vistelse <em>valfritt</em></span><textarea rows={3} placeholder="Vad för dig till Studentgården?" maxLength={1000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></label>
      {state === "error" && <p className="form-error"><XCircle /> {error}</p>}
      <button className="submit-button" disabled={state === "loading" || overlaps}>{state === "loading" ? <Loader2 className="spin" /> : <Mail />} {overlaps ? "Datum ej tillgängliga" : "Skicka bokningsförfrågan"}</button>
      <p className="form-note"><ShieldCheck /> Ingen betalning tas ut. Din vistelse bekräftas först efter godkännande.</p>
    </form>
  );
}

function PublicSite() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    const end = toIsoDate(addDays(new Date(), 370));
    fetch(`/api/availability?start=${toIsoDate(new Date())}&end=${end}`).then((r) => r.ok ? r.json() : Promise.reject()).then((data) => setBookings(data.bookings)).catch(() => undefined);
  }, []);
  return (
    <div id="top">
      <Header />
      <main>
        <section className="hero" id="house">
          <div className="hero-copy">
            <p className="eyebrow"><span /> EN STUDENTGÅRD VID HAVET</p>
            <h1>Rum att andas.<br /><em>Plats att höra hemma.</em></h1>
            <p className="hero-intro">En omtyckt studentgård i historiska Skanör — till för läsveckoslut, gemensamma måltider och lugna morgnar vid kusten.</p>
            <div className="hero-actions"><a className="primary-button" href="#availability">Se lediga datum <ArrowRight /></a><a className="text-link" href="#story">Upptäck huset <ArrowRight /></a></div>
            <div className="hero-details"><span><MapPin /> Skanör, Sverige</span><span><Waves /> Nära havet</span><span><Users /> Plats för grupper</span></div>
          </div>
          <div className="hero-image-wrap"><img src={housePhoto} alt="Studentgården, ett vitt hus med rött tegeltak i Skanör" /><span className="image-note">STUDENTGÅRDEN<br /><small>HUSET LÄNGST UT PÅ VÄGEN</small></span></div>
        </section>

        <section className="intro-section" id="story">
          <div><p className="eyebrow">ETT HUS MED HISTORIA</p><h2>Enkla dagar,<br /><em>delade väl.</em></h2></div>
          <div><p>I utkanten av Skanör, där den gamla staden möter flommen, bodde en gång Edla och John af Klercker. Gården donerades till Lunds studenter för att ge ”tillfälle till studier, vila och rekreation under fri och otvungen samvaro”, och togs över 1939. Sedan dess har långa bord fyllts av mat och samtal, havet är aldrig långt borta, och det finns alltid plats för en till.</p><a className="text-link" href="#gallery">Ta en titt inomhus <ArrowRight /></a></div>
        </section>

        <section className="feature-grid">
          <article><span><BedDouble /></span><h3>Bo tillsammans</h3><p>Gemensamma sovsalar för grupper, allt under samma tak.</p></article>
          <article><span><CookingPot /></span><h3>Laga mat & samlas</h3><p>Ett generöst kök och långa matbord gjorda för måltider som sträcker sig in på kvällen.</p></article>
          <article><span><Waves /></span><h3>Nära naturen</h3><p>Kliv ut i öppna ängar, kustnära stigar och Skanörs lugna rytm.</p></article>
        </section>

        <section className="availability-section" id="availability">
          <div className="availability-copy"><p className="eyebrow">TILLGÄNGLIGHET</p><h2>Hitta din<br /><em>tid härifrån.</em></h2><p>Gröna datum är öppna för förfrågningar. Välj ett ankomstdatum i kalendern, fyll i dina uppgifter, så återkommer gårdens personal till dig.</p><div className="process-list"><span><b>1</b>Välj dina datum</span><span><b>2</b>Skicka en förfrågan</span><span><b>3</b>Få en bekräftelse</span></div></div>
          <div className="booking-layout"><MiniCalendar bookings={bookings} selected={selected} onSelect={setSelected} /><BookingForm bookings={bookings} initialStart={selected} /></div>
        </section>

        <section className="pricing-section" id="priser">
          <div className="section-heading"><div><p className="eyebrow">PRISER</p><h2>Vad det<br /><em>kostar.</em></h2></div><p>Bokningsformuläret ovan gäller rum under sommarpensionatet. Vill ni hyra hela gården som grupp eller förening under resten av året, se kontaktuppgifterna nedan.</p></div>
          <div className="pricing-grid">
            <article className="price-card">
              <h3>Sommarpensionatet</h3>
              <p className="price-note">Enskilda rum, per person och natt.</p>
              <table>
                <tbody>
                  <tr><td>Medlem i Studentlund</td><td>295 kr vardag<br />345 kr fre–lör &amp; lör–sön</td></tr>
                  <tr><td>Särskild medlem i Akademiska Föreningen</td><td>595 kr vardag<br />645 kr fre–lör &amp; lör–sön</td></tr>
                </tbody>
              </table>
              <ul className="price-extra">
                <li><span>Medlemsavgift</span><span>220 kr</span></li>
                <li><span>Högsäsongstillägg</span><span>150 kr</span></li>
                <li><span>Enkelrumstillägg</span><span>120 kr</span></li>
                <li><span>Matgäster</span><span>100 kr</span></li>
                <li><span>Matgäster helg</span><span>120 kr</span></li>
              </ul>
            </article>
            <article className="price-card">
              <h3>Grupper &amp; föreningar</h3>
              <p className="price-note">Hela gården, dygnshyra under terminstid.</p>
              <table>
                <tbody>
                  <tr><td>Medlem i Studentlund</td><td>4 000 kr vardag<br />6 000 kr fre–lör &amp; lör–sön</td></tr>
                  <tr><td>Särskild medlem i Akademiska Föreningen</td><td>6 000 kr vardag<br />8 000 kr fre–lör &amp; lör–sön</td></tr>
                </tbody>
              </table>
              <p className="price-contact-note">Gruppbokningar under terminstid sker inte via formuläret ovan — kontakta oss direkt för bokning och frågor:</p>
              <p className="price-contact-info"><Mail size={14} /> E-post: anna.toorn@af.lu.se<br />Telefon: 046-38 49 00</p>
            </article>
          </div>
        </section>

        <section className="gallery-section" id="gallery">
          <div className="section-heading"><div><p className="eyebrow">EN TITT INOMHUS</p><h2>Känn dig<br /><em>som hemma.</em></h2></div><p>Fullt av karaktär, värme och spår av alla som bott här förut.</p></div>
          <div className="gallery-grid">
            <figure className="gallery-large"><img src={diningPhoto} alt="Den solbelysta matsalen med långa träbord" /><figcaption>MATSALEN <span>01</span></figcaption></figure>
            <figure><img src={loungePhoto} alt="Det röda sällskapsrummet med en piano" /><figcaption>SALONGEN <span>02</span></figcaption></figure>
            <figure><img src={kitchenPhoto} alt="Matlagning tillsammans i husets kök" /><figcaption>KÖKET <span>03</span></figcaption></figure>
          </div>
        </section>

        <section className="landscape-quote"><img src={meadowPhoto} alt="Kor som betar på ängen nära Studentgården" /><div><Sparkles /><blockquote>”Vissa platser ber dig sakta ner. Det här är en av dem.”</blockquote><p>ÄNGARNA I SKANÖR</p></div></section>
        <section className="cta-section"><img src={pathPhoto} alt="En kustnära stig bland vildrosor" /><div><p className="eyebrow">DIN VISTELSE BÖRJAR HÄR</p><h2>Kom och skapa<br /><em>minnen.</em></h2><a className="light-button" href="#availability">Se lediga datum <ArrowRight /></a></div></section>
      </main>
      <footer><div><Logo /><p>En studentgård vid havet,<br />delad sedan 1939.</p></div><div><strong>UTFORSKA</strong><a href="#house">Huset</a><a href="#availability">Tillgänglighet</a><a href="#priser">Priser</a><a href="#gallery">Galleri</a></div><div><strong>KONTAKT</strong><a href="mailto:hello@studentgarden.se">hello@studentgarden.se</a><span>Skanör, Sverige</span><span>Grupper: anna.toorn@af.lu.se</span></div><div className="footer-bottom"><span>© {new Date().getFullYear()} Studentgården</span></div></footer>
    </div>
  );
}

function ManualBookingForm({ token, onCreated, onCancel }: { token: string; onCreated: (booking: Booking) => void; onCancel: () => void }) {
  const tomorrow = toIsoDate(addDays(new Date(), 1));
  const [form, setForm] = useState({ startDate: tomorrow, endDate: toIsoDate(addDays(fromIsoDate(tomorrow), 1)), guests: 1, guestName: "", email: "", phone: "", message: "" });
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setState("loading"); setError("");
    try {
      const response = await fetch("/api/admin/bookings", { method: "POST", headers: { authorization: `Bearer ${token}`, "content-type": "application/json" }, body: JSON.stringify(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Kunde inte lägga till gästen.");
      onCreated(data.booking);
    } catch (reason) {
      setState("error");
      setError(reason instanceof Error ? reason.message : "Kunde inte lägga till gästen.");
    }
  }

  return (
    <form className="booking-form manual-booking-form" onSubmit={submit}>
      <div className="form-heading"><div><p className="eyebrow">MANUELL BOKNING</p><h3>Lägg till en gäst</h3></div><button type="button" className="text-link" onClick={onCancel}>Avbryt</button></div>
      <p className="price-note">För gäster som bokat via mejl eller telefon. Gästen läggs till som godkänd direkt.</p>
      <div className="field-row">
        <label><span>Ankomst</span><input type="date" required value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
        <label><span>Avresa</span><input type="date" required min={form.startDate} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label>
      </div>
      <label className="guest-control"><span>Gäster</span><div><button type="button" aria-label="Ta bort gäst" onClick={() => setForm({ ...form, guests: Math.max(1, form.guests - 1) })}><Minus /></button><strong>{form.guests}</strong><button type="button" aria-label="Lägg till gäst" onClick={() => setForm({ ...form, guests: Math.min(24, form.guests + 1) })}><Plus /></button></div></label>
      <div className="field-row">
        <label><span>Gästens namn</span><input required value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} placeholder="Fullständigt namn" /></label>
        <label><span>E-post</span><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="du@exempel.se" /></label>
      </div>
      <label><span>Telefon <em>valfritt</em></span><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+46" /></label>
      <label><span>Anteckning <em>valfritt</em></span><textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="T.ex. bokad via mejl den ..." /></label>
      {state === "error" && <p className="form-error"><XCircle /> {error}</p>}
      <button className="submit-button" disabled={state === "loading"}>{state === "loading" ? <Loader2 className="spin" /> : <Check />} Lägg till och godkänn</button>
    </form>
  );
}

function AdminPortal({ onBack }: { onBack: () => void }) {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem("admin-token") || "");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"all" | BookingStatus>("pending");
  const [showManualForm, setShowManualForm] = useState(false);

  async function load(authToken: string) {
    setLoading(true); setError("");
    try {
      const response = await fetch("/api/admin/bookings", { headers: { authorization: `Bearer ${authToken}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Kunde inte logga in");
      setBookings(data.bookings); setToken(authToken); sessionStorage.setItem("admin-token", authToken);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Kunde inte logga in"); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (token) load(token); }, []);
  async function updateStatus(id: string, status: BookingStatus) {
    setLoading(true); setError("");
    try {
      const response = await fetch(`/api/admin/bookings/${id}`, { method: "PATCH", headers: { authorization: `Bearer ${token}`, "content-type": "application/json" }, body: JSON.stringify({ status }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Kunde inte uppdatera förfrågan");
      setBookings((items) => items.map((item) => item.id === id ? data.booking : item));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Kunde inte uppdatera förfrågan"); }
    finally { setLoading(false); }
  }

  if (!token || (error && bookings.length === 0)) return (
    <div className="admin-login">
      <button className="back-button" onClick={onBack}><ArrowLeft /> Tillbaka till webbplatsen</button>
      <form onSubmit={(e) => { e.preventDefault(); load(password); }}>
        <span className="login-icon"><LockKeyhole /></span><p className="eyebrow">STUDENTGÅRDEN</p><h1>Administration</h1><p>Logga in för att granska och hantera bokningsförfrågningar.</p>
        <label><span>Adminlösenord</span><input type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Ange lösenord" /></label>
        {error && <p className="form-error"><XCircle /> {error}</p>}
        <button className="submit-button" disabled={loading}>{loading ? <Loader2 className="spin" /> : <LockKeyhole />} Logga in</button>
      </form>
    </div>
  );

  const visible = bookings.filter((item) => filter === "all" || item.status === filter);
  const pending = bookings.filter((item) => item.status === "pending").length;
  const upcoming = bookings.filter((item) => item.status === "approved" && item.endDate >= toIsoDate(new Date())).length;
  return (
    <div className="admin-page">
      <aside><Logo /><nav><button className="active"><CalendarDays /> Bokningsförfrågningar {pending > 0 && <b>{pending}</b>}</button><button onClick={onBack}><DoorOpen /> Visa webbplats</button></nav><div><p>Inloggad som</p><strong>Husadministratör</strong><button onClick={() => { sessionStorage.removeItem("admin-token"); setToken(""); }}>Logga ut</button></div></aside>
      <main>
        <header><div><p className="eyebrow">HUSADMIN</p><h1>Bokningsförfrågningar</h1><p>Granska förfrågningar och håll kalendern uppdaterad.</p></div><div className="header-actions"><button className="view-site" onClick={() => setShowManualForm((v) => !v)}><Plus size={15} /> Lägg till gäst</button><button className="view-site" onClick={onBack}>Visa webbplats <ArrowRight /></button></div></header>
        {showManualForm && <ManualBookingForm token={token} onCancel={() => setShowManualForm(false)} onCreated={(booking) => { setBookings((items) => [booking, ...items]); setShowManualForm(false); }} />}
        <section className="admin-stats"><article><span><Clock3 /></span><div><strong>{pending}</strong><p>Väntar på granskning</p></div></article><article><span><CheckCircle2 /></span><div><strong>{upcoming}</strong><p>Kommande vistelser</p></div></article><article><span><Users /></span><div><strong>{bookings.filter((b) => b.status === "approved").reduce((sum, b) => sum + b.guests, 0)}</strong><p>Bekräftade gäster</p></div></article></section>
        <div className="filter-bar"><div>{(["pending", "approved", "declined", "all"] as const).map((value) => <button className={filter === value ? "active" : ""} onClick={() => setFilter(value)} key={value}>{FILTER_LABEL[value]} {value !== "all" && <span>{bookings.filter((b) => b.status === value).length}</span>}</button>)}</div><span>{visible.length} {visible.length === 1 ? "förfrågan" : "förfrågningar"}</span></div>
        {error && <p className="admin-error"><XCircle /> {error}</p>}
        <section className="request-list">
          {visible.map((booking) => (
            <article className="request-card" key={booking.id}>
              <div className="request-date"><strong>{formatDate(booking.startDate, { day: "2-digit" })}</strong><span>{formatDate(booking.startDate, { month: "short" }).toUpperCase()}</span><i /><small>{Math.round((fromIsoDate(booking.endDate).getTime() - fromIsoDate(booking.startDate).getTime()) / DAY)} nätter</small></div>
              <div className="request-main"><div className="request-title"><div><h3>{booking.guestName}</h3><p>{booking.email}{booking.phone ? ` · ${booking.phone}` : ""}</p></div><span className={`status status-${booking.status}`}>{booking.status === "pending" && <Clock3 />}{booking.status === "approved" && <CheckCircle2 />}{booking.status === "declined" && <XCircle />}{STATUS_LABEL[booking.status]}</span></div><div className="request-meta"><span><CalendarDays /> {formatDate(booking.startDate, { day: "numeric", month: "long" })} — {formatDate(booking.endDate, { day: "numeric", month: "long", year: "numeric" })}</span><span><Users /> {booking.guests} gäster</span></div>{booking.message && <blockquote>”{booking.message}”</blockquote>}<div className="request-footer"><small>Skickad {new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(booking.createdAt))}</small>{booking.status === "pending" && <div><button className="decline" disabled={loading} onClick={() => updateStatus(booking.id, "declined")}><X /> Neka</button><button className="approve" disabled={loading} onClick={() => updateStatus(booking.id, "approved")}><Check /> Godkänn vistelse</button></div>}</div></div>
            </article>
          ))}
          {visible.length === 0 && <div className="empty-state"><CalendarDays /><h3>Inga {filter === "all" ? "" : FILTER_LABEL[filter].toLowerCase()} förfrågningar</h3><p>Nya bokningsförfrågningar visas här.</p></div>}
        </section>
      </main>
    </div>
  );
}

export function App() {
  const [admin, setAdmin] = useState(() => window.location.search.includes("admin"));
  function showAdmin(value: boolean) { setAdmin(value); history.pushState({}, "", value ? "?admin" : window.location.pathname); window.scrollTo(0, 0); }
  return admin ? <AdminPortal onBack={() => showAdmin(false)} /> : <PublicSite />;
}

export default App;
